import {createStyles} from '@mantine/core';
import React, {ComponentProps, useEffect} from 'react';

import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import Spinner from 'ui-library/Spinner';

import StepConfigEditorPane from './StepConfigEditorPane';
import useLiveStepShape from './useLiveStepShape';

const NO_INPUTS: ReadonlyArray<string> = [];

interface LiveShapeStepConfigEditorPaneProps extends Omit<
  ComponentProps<typeof StepConfigEditorPane>,
  'configShape' | 'disabledFields'
> {
  /**
   * The names of the step's inputs with saved incoming edges. The backend can shape the config by them, e.g.
   * Opentrons disables protocol selection while a protocol file input is connected.
   */
  connectedInputNames?: ReadonlyArray<string>;
  /**
   * Called with true while the live shape for a selection the user changed (e.g. the variables of a newly selected
   * method) is loading, and false once it settles. The config's runtime values still belong to the previous
   * selection in between, so callers should block saving.
   */
  onSelectionLoadingChange?: (isSelectionLoading: boolean) => void;
}

/**
 * A `StepConfigEditorPane` that, for step types whose options come from an instrument (`hasSavedShape` in their UI
 * schema), shows the saved settings right away and loads the instrument's options in the background. Other step
 * types get a plain `StepConfigEditorPane`.
 */
export default function LiveShapeStepConfigEditorPane({
  connectedInputNames = NO_INPUTS,
  onSelectionLoadingChange,
  ...paneProps
}: LiveShapeStepConfigEditorPaneProps): JSX.Element {
  const liveStepShape = useLiveStepShape({
    stepType: paneProps.stepType,
    stepVersion: paneProps.stepVersion,
    stepId: paneProps.stepId,
    clientStateId: paneProps.clientStateId,
    config: paneProps.config,
    setConfig: paneProps.setConfig,
    readOnly: paneProps.readOnly === true,
    connectedInputNames,
  });
  const isSelectionLoading = liveStepShape?.isSelectionLoading === true;
  useEffect(() => {
    onSelectionLoadingChange?.(isSelectionLoading);
  }, [isSelectionLoading, onSelectionLoadingChange]);
  useEffect(
    () => () => {
      onSelectionLoadingChange?.(false);
    },
    [onSelectionLoadingChange]
  );
  const {classes} = useStyles();

  if (liveStepShape == null) {
    return <StepConfigEditorPane {...paneProps} />;
  }

  return (
    <>
      {liveStepShape.loadingMessage != null && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          hideIcon={true}
          text={
            <>
              <Spinner aria-hidden={true} classNames={classes.bannerSpinner} />
              {liveStepShape.loadingMessage}
            </>
          }
        />
      )}
      {liveStepShape.warnings.map((warning) => (
        <InlineBanner key={warning} bannerType={InlineBannerType.WARNING} text={warning} />
      ))}
      <StepConfigEditorPane
        {...paneProps}
        config={liveStepShape.config}
        setConfig={liveStepShape.setConfig}
        configShape={liveStepShape.configShape}
        disabledFields={liveStepShape.disabledFields}
      />
    </>
  );
}

const useStyles = createStyles((theme) => ({
  // Takes the place of the banner icon. `&&` outranks the global spinner margin.
  bannerSpinner: {
    '&&': {
      margin: 0,
      marginRight: theme.spacing.sm,
      verticalAlign: 'middle',
    },
  },
}));
